import React from 'react';
import { Box } from '@mui/material';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { FieldValues, useWatch, useFormContext, Path, PathValue } from 'react-hook-form';
import {
  CreateAlertConfigType,
  OperatorConfigType,
  RuleBlockConfigType,
  RuleGroupConfigType
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/schema/AlertBuilderSchemaTypes';
import RuleBlock from './RuleBlock';
import ExpandCollapseIcon from './ui/ExpandCollapseIcon';
import DeleteIcon from './ui/DeleteIcon';
import RuleButton from './RuleButton';
import RuleBlockOperatorDropdown from './RuleBlockOperatorDropdown';
import {
  createDefaultRuleBlock,
  createDefaultRuleBlockOperator,
  DEFAULT_LOGICAL_OPERATOR,
  getDominantLogicalOperator
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/utils';
import useCurrentAndFutureWeekId from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/hooks/useCurrentAndFutureWeekId';

type Props<T extends FieldValues> = {
  groupIndex: number;
  metricsOptions: string[];
};

function RuleGroup<T extends FieldValues = CreateAlertConfigType>({ groupIndex, metricsOptions }: Props<T>) {
  const { setValue } = useFormContext<T>();
  const allWeekIds = useCurrentAndFutureWeekId();

  // Base paths for this rule group
  const basePath = `queryJSON.rules.ruleGroups.${groupIndex}` as Path<T>;
  const ruleBlocksPath = `${basePath}.ruleBlocks` as Path<T>;
  const operatorsPath = `${basePath}.operators` as Path<T>;

  // Paths for all rule groups (for deletion logic)
  const allRuleGroupsPath = 'queryJSON.rules.ruleGroups' as Path<T>;
  const allGroupOperatorsPath = 'queryJSON.rules.groupOperators' as Path<T>;

  // Watch the group data and all rule groups
  const group = useWatch({ name: basePath }) as RuleGroupConfigType;
  const allRuleGroups = useWatch({ name: allRuleGroupsPath }) || [];
  const allGroupOperators = useWatch({ name: allGroupOperatorsPath }) || [];
  const [isCollapsed, setIsCollapsed] = React.useState(false);

  // Add a new rule block to this group
  const addRuleToGroup = () => {
    const newRuleBlock = createDefaultRuleBlock(allWeekIds);
    const updatedRuleBlocks = [...group.ruleBlocks, newRuleBlock] as RuleBlockConfigType[];
    setValue(ruleBlocksPath, updatedRuleBlocks as PathValue<T, typeof ruleBlocksPath>);

    // Automatically add operator if this is the second or later rule block
    if (group.ruleBlocks.length > 0) {
      const operatorIndex = group.ruleBlocks.length - 1; // Position between existing and new block

      // Use the utility function to determine the logical operator
      const dominantOperator = getDominantLogicalOperator(group.operators);
      const logicalOperatorType = dominantOperator || DEFAULT_LOGICAL_OPERATOR;

      const newOperator = createDefaultRuleBlockOperator(logicalOperatorType);
      const updatedOperators = [...group.operators, newOperator] as OperatorConfigType[];
      setValue(operatorsPath, updatedOperators as PathValue<T, typeof operatorsPath>);
    }
  };

  // Delete this rule group
  const deleteRuleGroup = () => {
    const updatedRuleGroups = allRuleGroups.filter((ruleGroup, index) => index !== groupIndex);
    const updatedGroupOperators = allGroupOperators.slice(0, Math.max(0, updatedRuleGroups.length - 1));

    setValue(allRuleGroupsPath, updatedRuleGroups as PathValue<T, typeof allRuleGroupsPath>);
    setValue(allGroupOperatorsPath, updatedGroupOperators as PathValue<T, typeof allGroupOperatorsPath>);
  };

  // Determine if this group can be deleted (not first group and there are multiple groups)
  const canDeleteGroup = allRuleGroups.length > 1 && groupIndex > 0;

  return (
    <Box
      sx={{
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        padding: '16px',
        backgroundColor: '#f8f9f9'
      }}
    >
      {/* Rule Group Header */}
      <Flex justifyContent="space-between" alignItems="center" style={{ marginBottom: '16px' }}>
        <Flex alignItems="center" gap={8}>
          <ExpandCollapseIcon isCollapsed={isCollapsed} onToggle={() => setIsCollapsed(!isCollapsed)} />
          <Text variant="h5" sx={{ fontWeight: 600 }}>
            Rule Group {groupIndex + 1}
          </Text>
        </Flex>

        <Flex alignItems="center" gap={8}>
          <Text variant="body2" sx={{ color: '#666' }}>
            {group.ruleBlocks.length} rule{group.ruleBlocks.length !== 1 ? 's' : ''}
          </Text>
          <DeleteIcon onDelete={deleteRuleGroup} tooltip="Delete rule group" show={canDeleteGroup} />
        </Flex>
      </Flex>

      {/* Collapsible Content */}
      {!isCollapsed && (
        <>
          {/* Rule Blocks */}
          {group.ruleBlocks.map((block, blockIndex) => (
            <React.Fragment key={blockIndex}>
              {/* Rule Block Operator (between rule blocks within a group) */}
              {blockIndex > 0 && (
                <Flex justifyContent="center" style={{ margin: '8px 0' }}>
                  <Box sx={{ minWidth: '100px' }}>
                    <RuleBlockOperatorDropdown<T> groupIndex={groupIndex} operatorIndex={blockIndex - 1} />
                  </Box>
                </Flex>
              )}

              <RuleBlock<T> groupIndex={groupIndex} blockIndex={blockIndex} metricsOptions={metricsOptions} />
            </React.Fragment>
          ))}

          {/* Add Rule to Group Button */}
          <RuleButton onClick={addRuleToGroup}>+ Add Rule to Group</RuleButton>
        </>
      )}
    </Box>
  );
}

export default RuleGroup;
